import React, { useState, useEffect } from 'react';
import {
    Container, Paper, Title, Text, TextInput, Button, Group, Stack, Card, Avatar,
    Badge, Divider, Box, Grid, Select, MultiSelect, SegmentedControl, ThemeIcon, Alert, SimpleGrid, Accordion, UnstyledButton
} from '@mantine/core';
import {
    IconSearch, IconUser, IconActivity, IconTarget, IconBarbell,
    IconAlertCircle, IconHome, IconBuildingStore, IconUsers, IconCheck,
    IconBrandBumble, IconChevronDown, IconChevronRight, IconBolt, IconClock, IconFlame
} from '@tabler/icons-react';

import { collection, getDocs, query, where, collectionGroup, doc, getDoc, writeBatch, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { db, functions } from '../../../firebase/FirebaseCredential';
import { httpsCallable } from 'firebase/functions';
import { useAppSelector } from '../../../hooks/hooks';
import { RootState } from '../../../store/store';
import { fetchJsonFile } from '../ExerciseList';
import BenchLoader from "../../UIComponent/BenchLoader/BenchLoader";
import BenchBreadcrumbs from '../../UIComponent/Components/BenchBreadcrumbs';
import { ROUTES } from '../../../routes/routesPaths';
import ErrorBoundary from '../../ErrorBoundary/ErrorBoundary';
import BenchHeading from '../../pages/UI/BenchHeading';
import BenchTopHeader from '../../UIComponent/Components/BenchTopHeader';
import BenchCard from '../../UIComponent/Components/BenchCard';
import { toast } from 'react-toastify';

/**
 * INTELLIGENT MEMBER ROUTINE GENERATOR - PHASE 1
 * This module handles member validation, data fetching, and rule-based exercise filtering.
 */

interface Exercise {
    name: string;
    exercise_id: string;
    equipment: string[];
    primary_muscle: string[];
    secondary_muscle?: string[];
    category?: string;
    level?: string;
    mechanic?: string;
    movement_pattern?: string;
    sets?: string;
    reps_time?: string;
    type?: string;
    timeSeconds?: number;
    calories?: number;
    restPerSet?: number;
    suggestedWeight?: string;
}

interface Member {
    uid: string;
    name: string;
    age: number;
    gender: string;
    bmi: number;
    weight: number;
    height?: number;
    goal?: string;
    level?: string;
    workout_location?: string;
    equipment?: string[];
    activity_level?: string;
    injuries?: string[];
    target_weight?: number;
}



interface MemberRoutineGeneratorProps {
    isModalMode?: boolean;
    preselectedMember?: any;
    onGenerateSuccess?: () => void;
    onClose?: () => void;
}

const MemberRoutineGenerator: React.FC<MemberRoutineGeneratorProps> = ({ isModalMode, preselectedMember, onGenerateSuccess, onClose }) => {
    // --- AUTH & CONTEXT ---
    const companyId = useAppSelector((state: RootState) => state.session.company_id);
    const { branchesList } = useAppSelector((state: RootState) => state.branchList);

    // --- STATE MANAGEMENT ---
    const [member, setMember] = useState<Member | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [missingUidMemberName, setMissingUidMemberName] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    // Branch & Member Selection
    const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
    const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
    const [branchMembers, setBranchMembers] = useState<any[]>([]);

    const [masterExercises, setMasterExercises] = useState<Exercise[]>([]);
    const [filteredExercises, setFilteredExercises] = useState<any[]>([]);
    const [personalBests, setPersonalBests] = useState<any[]>([]);

    // Routine Inputs
    const [inputs, setInputs] = useState({
        goal: "",
        level: '',
        equipment: [] as string[],
        daysPerWeek: '',
        duration: '',
        place: ''
    });

    const [targetDays, setTargetDays] = useState<string[]>([]);

    // --- EFFECTS ---
    useEffect(() => {
        const loadExercises = async () => {
            setLoading(true);
            try {
                const data = await fetchJsonFile();
                if (data && Array.isArray(data)) {
                    setMasterExercises(data as Exercise[]);
                }
            } catch (err) {
                console.error("Error loading exercises:", err);
            } finally {
                setLoading(false);
            }
        };
        loadExercises();
    }, []);

    useEffect(() => {
        if (!isModalMode) {
            setFilteredExercises([]);
            setMember(null);
            setSelectedMemberId(null);
            if (selectedBranch) {
                fetchMembersOfBranch(selectedBranch);
            } else {
                setBranchMembers([]);
            }
        }
    }, [selectedBranch, isModalMode]);

    useEffect(() => {
        if (!isModalMode && !selectedBranch && branchesList?.length > 0) {
            const defaultBranch = branchesList.find((b: any) => b.id !== "all" && b.name?.toLowerCase() !== "all branches");
            if (defaultBranch) {
                setSelectedBranch(defaultBranch.id);
            }
        }
    }, [branchesList, isModalMode, selectedBranch]);

    useEffect(() => {
        if (isModalMode && preselectedMember) {
            setSelectedBranch(preselectedMember.branch_id);
            setSelectedMemberId(preselectedMember.id);
            processMemberData(preselectedMember);
        }
    }, [isModalMode, preselectedMember]);

    /**
     * Fetch all members for a specific branch
     */
    const fetchMembersOfBranch = async (branchId: string) => {
        setLoading(true);
        try {
            const membersRef = collection(db, "branch", branchId, "members");
            const q = query(membersRef, where("company_id", "==", companyId));
            const snapshot = await getDocs(q);
            const memberList = snapshot.docs.map(doc => ({
                value: doc.id,
                label: `${doc.data().first_name} ${doc.data().last_name}`,
                ...doc.data()
            }));
            setBranchMembers(memberList);
        } catch (err) {
            console.error("Error fetching members:", err);
            setError("Failed to fetch members for this branch");
        } finally {
            setLoading(false);
        }
    };

    const processMemberData = async (memberData: any) => {
        if (!memberData) return;
        setError(null);
        setLoading(true);
        try {
            // Step 1 & 2: Pass UID and Mobile to Cloud Function to securely resolve UID and fetch data
            const mobileToSearch = memberData.mobile_number || memberData.mobile || null;
            const getProfileAdmin = httpsCallable(functions, 'fetchFitnessProfileAdmin');
            const response = await getProfileAdmin({
                uid: memberData.uid,
                mobile_number: mobileToSearch
            });

            const profileData = (response.data as any) || {};
            const targetUid = profileData.resolvedUid;

            if (!targetUid) {
                setMissingUidMemberName(`${memberData.first_name || ''} ${memberData.last_name || ''}`);
                setError(`Member "${memberData.first_name || ''} ${memberData.last_name || ''}" does not have a linked user account (UID).`);
                setMember(null);
                setLoading(false);
                return;
            } else {
                setMissingUidMemberName(null);
            }

            const personalBestsData = profileData.sub_collections?.personal_best || profileData.sub_collections?.personalbest || profileData.personalbest || [];

            const mappedMember: Member = {
                uid: targetUid,
                name: `${memberData.first_name || ''} ${memberData.last_name || ''}`,
                age: profileData.age || memberData.age,
                gender: profileData.gender || memberData.gender,
                bmi: profileData.bmi || memberData.bmi,
                weight: profileData.weight || memberData.weight,
                height: profileData.height || memberData.height,
                goal: profileData.goal || memberData.goal,
                level: profileData.level || memberData.level,
                workout_location: profileData.workout_location || memberData.workout_location,
                equipment: profileData.equipment || memberData.equipment || [],
                activity_level: profileData.activity_level || memberData.activity_level,
                injuries: profileData.injuries || memberData.injuries || [],
                target_weight: profileData.target_weight || profileData.targetWeight || memberData.target_weight || memberData.targetWeight
            };

            setMember(mappedMember);
            setPersonalBests(personalBestsData);
            fetchFitnessProfile(mappedMember);
        } catch (err) {
            console.error("Error setting up member:", err);
            setError("Failed to load member profile data.");
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handle Member Selection from Dropdown
     */
    const handleMemberSelect = async (memberId: string | null) => {
        setFilteredExercises([]);
        if (!memberId) {
            setSelectedMemberId(null);
            setMember(null);
            setError(null);
            setMissingUidMemberName(null);
            return;
        }
        setSelectedMemberId(memberId);
        const memberData = branchMembers.find(m => m.value === memberId);
        if (memberData) {
            processMemberData(memberData);
        }
    };

    const fetchFitnessProfile = (data: Member) => {
        setInputs(prev => {
            // Normalize Goal
            let normalizedGoal = prev.goal;
            if (data.goal) {
                const rawGoal = data.goal.toLowerCase().replace(/\s+/g, '_');
                if (['weight_loss', 'muscle_gain', 'strength', 'endurance', 'maintain_fitness'].includes(rawGoal)) {
                    normalizedGoal = rawGoal;
                } else if (rawGoal === 'weightloss') normalizedGoal = 'weight_loss';
                else if (rawGoal === 'musclegain') normalizedGoal = 'muscle_gain';
            }

            // Normalize Level (use activity_level as fallback)
            let normalizedLevel = prev.level;
            const rawLevel = (data.level || data.activity_level || '').toLowerCase();
            if (['beginner', 'intermediate', 'advanced'].includes(rawLevel)) {
                normalizedLevel = rawLevel;
            }

            // Normalize Place
            let normalizedPlace = prev.place || 'gym';
            const rawPlace = (data.workout_location || '').toLowerCase();
            if (['gym', 'home', 'travel'].includes(rawPlace)) {
                normalizedPlace = rawPlace;
            }

            return {
                ...prev,
                goal: normalizedGoal,
                level: normalizedLevel,
                equipment: data.equipment || [],
                place: normalizedPlace,
                daysPerWeek: '',
                duration: ''
            };
        });
        setTargetDays([]);
    };


    const handleDaysPerWeekChange = (val: string) => {
        setInputs({ ...inputs, daysPerWeek: val });
        
        const numDays = parseInt(val, 10);
        switch (numDays) {
            case 3:
                setTargetDays(['Monday', 'Wednesday', 'Friday']);
                break;
            case 4:
                setTargetDays(['Monday', 'Tuesday', 'Thursday', 'Saturday']);
                break;
            case 5:
                setTargetDays(['Monday', 'Tuesday', 'Wednesday', 'Friday', 'Saturday']);
                break;
            case 6:
                setTargetDays(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']);
                break;
            case 7:
                setTargetDays(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']);
                break;
            default:
                setTargetDays([]);
        }
    };

    /**
     * Step 4: Intelligent Filtering Engine
     * Delegates to the WorkoutEngine for real programming.
     */
    const filterExercises = async () => {
        if (!member) {
            toast.warn("Please wait for member profile to load.");
            return;
        }

        if (!inputs.goal || !inputs.level || !inputs.daysPerWeek || !inputs.duration || !inputs.place) {
            toast.warn("Please fill in all mandatory constraints before generating.");
            return;
        }

        const requiredDays = Number(inputs.daysPerWeek) || 0;
        if (requiredDays > 0 && targetDays.length !== requiredDays) {
            toast.warn(`Please select exactly ${requiredDays} Target Days.`);
            return;
        }

        setLoading(true);
        try {
            const generateRoutineFn = httpsCallable(functions, 'generateRoutine');
            const response = await generateRoutineFn({
                member,
                inputs,
                targetDays,
                personalBests,
                companyId: preselectedMember ? preselectedMember.company_id : undefined,
                selectedBranch: selectedBranch || "",
                selectedMemberId: selectedMemberId || "",
                previewOnly: !isModalMode
            });

            const generatedDays = (response.data as any).generatedDays;

            if (generatedDays) {
                if (isModalMode) {
                    toast.success("Routine successfully generated and assigned!");
                    if (onGenerateSuccess) onGenerateSuccess();
                } else {
                    // Forward all engine output fields to state
                    setFilteredExercises(
                        generatedDays.map((day: any) => ({
                            dayName: day.dayName,
                            splitFocus: day.splitFocus,
                            emphasis: day.emphasis,
                            fatigue: day.fatigue,
                            exercises: day.exercises,
                        }))
                    );
                    toast.success("Routine successfully generated! Please review and click Save.");
                }
            }
        } catch (err) {
            console.error("Cloud Function Error:", err);
            toast.error("Failed to generate routine via Cloud Function.");
        } finally {
            setLoading(false);
        }
    };

    const saveRoutine = async () => {
        if (!member || filteredExercises.length === 0) {
            toast.warn("No routine generated to save.");
            return;
        }

        const requiredDays = Number(inputs.daysPerWeek) || 0;
        if (requiredDays > 0 && targetDays.length !== requiredDays) {
            toast.warn(`Please select exactly ${requiredDays} Target Days.`);
            return;
        }

        setLoading(true);
        try {
            const generateRoutineFn = httpsCallable(functions, 'generateRoutine');
            await generateRoutineFn({
                member,
                inputs,
                targetDays,
                personalBests,
                companyId: companyId || "",
                selectedBranch: selectedBranch || "",
                selectedMemberId: selectedMemberId || "",
                previewOnly: false,
                preGeneratedDays: filteredExercises
            });

            if (isModalMode) {
                toast.success("Routine successfully assigned!");
                if (onGenerateSuccess) onGenerateSuccess();
            } else {
                toast.success("Routine successfully saved and assigned to the member");
                
                // Reset member and constraints so the UI hides member data after save
                setMember(null);
                setSelectedMemberId(null);
                setPersonalBests([]);
                setInputs({
                    goal: "",
                    level: '',
                    equipment: [],
                    daysPerWeek: '',
                    duration: '',
                    place: ''
                });
                setTargetDays([]);
            }
            setFilteredExercises([]);

        } catch (err) {
            console.error("Cloud Function Error:", err);
            toast.error("Failed to save routine via Cloud Function.");
        } finally {
            setLoading(false);
        }
    };


    // --- UI RENDERING ---

    const getDaysOptions = (level: string) => {
        switch (level?.toLowerCase()) {
            case 'beginner': return [{ value: '3', label: '3 Days' }, { value: '4', label: '4 Days' }];
            case 'intermediate': return [{ value: '4', label: '4 Days' }, { value: '5', label: '5 Days' }];
            case 'advanced': return [{ value: '5', label: '5 Days' }, { value: '6', label: '6 Days' }];
            default: return [{ value: '3', label: '3 Days' }, { value: '4', label: '4 Days' }, { value: '5', label: '5 Days' }];
        }
    };

    const breadcrumbs = [
        { label: "Routine", path: "/routineplan" },
        { label: "Member Routine Generator" },
    ];

    if (isModalMode) {
        return (
            <ErrorBoundary>
                <BenchLoader isLoading={loading} />
                <BenchCard>
                    {/* {error && (
                        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" radius="md" mb="md">
                            {error}
                        </Alert>
                    )} */}
                    <Group position="apart" mb="md">
                        <Title order={4}>Routine Constraints</Title>
                    </Group>

                    <Grid>
                        <Grid.Col span={6}>
                            <Select
                                label="Selection Goal"
                                data={[
                                    { value: 'weight_loss', label: 'Weight Loss' },
                                    { value: 'muscle_gain', label: 'Muscle Gain' },
                                    { value: 'strength', label: 'Strength' },
                                    { value: 'endurance', label: 'Endurance' },
                                    { value: 'maintain_fitness', label: 'Maintain Fitness' },
                                ]}
                                value={inputs.goal}
                                onChange={(val: string) => setInputs({ ...inputs, goal: val })}
                            />
                        </Grid.Col>
                        <Grid.Col span={6}>
                            <Select
                                label="Complexity Level"
                                data={[
                                    { value: 'beginner', label: 'Beginner' },
                                    { value: 'intermediate', label: 'Intermediate' },
                                    { value: 'advanced', label: 'Advanced' },
                                ]}
                                value={inputs.level}
                                onChange={(val: string) => {
                                    setInputs({ ...inputs, level: val });
                                }}
                            />
                        </Grid.Col>
                        <Grid.Col span={4}>
                            <Select
                                label="Days Per Week"
                                data={getDaysOptions(inputs.level)}
                                value={inputs.daysPerWeek}
                                onChange={handleDaysPerWeekChange}
                            />
                        </Grid.Col>
                        <Grid.Col span={4}>
                            <Select
                                label="Duration"
                                data={[
                                    { value: '30', label: '30' },
                                    { value: '45', label: '45' },
                                    { value: '60', label: '60' },
                                    { value: '60+', label: '60+' },
                                ]}
                                value={inputs.duration}
                                onChange={(val: string) => setInputs({ ...inputs, duration: val })}
                            />
                        </Grid.Col>
                        <Grid.Col span={4}>
                            <Box>
                                <Text size="sm" weight={500} mb={3}>Location</Text>
                                <SegmentedControl
                                    fullWidth
                                    data={[
                                        { label: 'Gym', value: 'gym' },
                                        { label: 'Home', value: 'home' },
                                        { label: 'Travel', value: 'travel' }
                                    ]}
                                    value={inputs.place}
                                    onChange={(val: string) => setInputs({ ...inputs, place: val })}
                                />
                            </Box>
                        </Grid.Col>
                        <Grid.Col span={12}>
                            <MultiSelect
                                label="Available Equipment"
                                placeholder="Select equipment..."
                                data={['Bodyweight', 'Dumbbell', 'Barbell', 'Kettlebell', 'Cable', 'Resistance Band', 'Machine', 'Medicine Ball']}
                                searchable
                                value={inputs.equipment}
                                onChange={(val: string[]) => setInputs({ ...inputs, equipment: val })}
                            />
                        </Grid.Col>
                        <Grid.Col span={12}>
                            <Box>
                                <Text size="sm" weight={500} mb={3}>Target Days <span style={{ color: '#fa5252' }}>*</span></Text>
                                <Group spacing="xs">
                                    {[
                                        { label: 'M', value: 'Monday' },
                                        { label: 'T', value: 'Tuesday' },
                                        { label: 'W', value: 'Wednesday' },
                                        { label: 'T', value: 'Thursday' },
                                        { label: 'F', value: 'Friday' },
                                        { label: 'S', value: 'Saturday' },
                                        { label: 'S', value: 'Sunday' },
                                    ].map((day, idx) => {
                                        const isSelected = targetDays.includes(day.value);
                                        return (
                                            <UnstyledButton
                                                key={`${day.value}-${idx}`}
                                                onClick={() => {
                                                    if (isSelected) {
                                                        setTargetDays(targetDays.filter(d => d !== day.value));
                                                    } else {
                                                        const requiredDays = Number(inputs.daysPerWeek) || 0;
                                                        if (requiredDays > 0 && targetDays.length >= requiredDays) {
                                                            toast.warn(`You can only select exactly ${requiredDays} Target Days.`);
                                                            return;
                                                        }
                                                        setTargetDays([...targetDays, day.value]);
                                                    }
                                                }}
                                                sx={(theme: any) => ({
                                                    width: 36, height: 36, borderRadius: '50%',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontWeight: 600, fontSize: theme.fontSizes.sm,
                                                    color: isSelected ? theme.white : theme.colors.gray[6],
                                                    backgroundColor: isSelected ? theme.colors.brand[4] : theme.colors.gray[1],
                                                    border: `1px solid ${isSelected ? theme.colors.brand[4] : theme.colors.gray[3]}`,
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': { backgroundColor: isSelected ? theme.colors.brand[5] : theme.colors.gray[2] }
                                                })}
                                            >
                                                {day.label}
                                            </UnstyledButton>
                                        )
                                    })}
                                </Group>
                                <Text size="xs" color="dimmed" mt={4}>
                                    {inputs?.place?.toLowerCase() === 'gym' 
                                        ? "Remaining days are rest days or light workout days." 
                                        : "Remaining days are rest days."}
                                </Text>
                            </Box>
                        </Grid.Col>
                    </Grid>

                    <Group position="right" mt="xl">
                        {onClose && <Button variant="outline" onClick={onClose}>Cancel</Button>}
                        <Button onClick={filterExercises} loading={loading}>Generate Routine</Button>
                    </Group>
                </BenchCard>
            </ErrorBoundary>
        );
    }

    return (
        <ErrorBoundary>
            <BenchLoader isLoading={loading} />
            <Container size="lg" > <Box
                        sx={(theme) => ({
                          position: "sticky",
                          top: 110, // Avoid overlapping the fixed navbar
                          zIndex: 100,
                          backgroundColor: "#FAFAFA",
                          paddingTop: theme.spacing.sm,
                          //paddingBottom: theme.spacing.xs,
                          marginTop: `-${theme.spacing.sm}`,
                        })}
                      >
                <Group position="apart">
                    <BenchBreadcrumbs links={breadcrumbs} />
                    <Group>
                        <Button variant="light" onClick={filterExercises} loading={loading}>
                            {filteredExercises.length > 0 ? "Regenerate Preview" : "Generate Preview"}
                        </Button>
                        {filteredExercises.length > 0 && (
                            <Button onClick={saveRoutine} loading={loading}>
                                Save Routine
                            </Button>
                        )}
                    </Group>
                </Group>
                <Divider my="md" /> </Box>

                {/* Header Row: Title on left, Dropdowns on right */}
                <Group position="apart" mb="xl">
                    <BenchTopHeader title="Member Routine Generator" icon={<IconBarbell />} />

                </Group><Group position='right' my="md">
                    <Select
                        placeholder="Select Branch"
                        icon={<IconBuildingStore size={14} />}
                        data={branchesList?.filter((b: any) => b.id !== "all" && b.name?.toLowerCase() !== "all branches").map((b: any) => ({ value: b.id, label: b.name })) || []}
                        value={selectedBranch}
                        onChange={setSelectedBranch}
                        clearable
                        sx={{ width: 200 }}
                        radius="xl"
                    /><Select
                        placeholder="Select Member"
                        icon={<IconUser size={14} />}
                        data={branchMembers || []}
                        value={selectedMemberId}
                        onChange={handleMemberSelect}
                        disabled={!selectedBranch && branchMembers.length === 0}
                        clearable
                        searchable
                        nothingFound="No members available in this branch"
                        sx={{ width: 250 }}
                        radius="xl"
                    />

                </Group>

                {error && (
                    <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" radius="md" mb="md">
                        {error}
                    </Alert>
                )}
                {missingUidMemberName && (
                    <Card shadow="sm" p="xl" radius="md" withBorder sx={{ textAlign: 'center', backgroundColor: '#fff', borderColor: '#ee374d', marginBottom: '20px' }}>
                        <IconAlertCircle size={50} color="#ee374d" style={{ marginBottom: 15 }} />
                        <Title order={3} mb="sm">Account Not Linked</Title>
                        <Text size="md" color="dimmed" mb="lg">
                            The member <b>{missingUidMemberName}</b> has not created an account on the Bench Fitness mobile app yet.
                            Routine generation requires an active mobile app account.
                        </Text>
                        <Button
                            color="dark"
                            size="md"
                            radius="xl"
                            leftIcon={<IconUser size={18} />}
                            sx={{ backgroundColor: '#ee374d', '&:hover': { backgroundColor: '#d92c40' } }}
                            onClick={() => setMissingUidMemberName(null)}
                        >
                            Ask them to download the Bench Mobile App
                        </Button>
                    </Card>
                )}

                {member ? (
                    <Grid gutter="xl">
                        {/* LEFT COLUMN - MEMBER DETAILS */}
                        <Grid.Col span={12} md={4}>
                            <Card radius="lg" p="xl" shadow="sm" withBorder sx={{ borderColor: '#f1f3f5', height: '100%' }}>
                                <Group spacing="md" noWrap mb="xl">
                                    <Avatar size={64} radius="xl" color="gray" sx={{ backgroundColor: '#e9ecef', color: '#495057', fontSize: '24px', fontWeight: 600 }}>
                                        {member.name.substring(0, 2).toUpperCase()}
                                    </Avatar>
                                    <Box>
                                        <Group spacing="xs" mb={4}>
                                            <Title order={4} sx={{ color: '#2c2e33' }}>{member.name}</Title>
                                            <Badge color="green" variant="light" size="sm">● Active Member</Badge>
                                        </Group>
                                        <Text size="xs" color="dimmed" mt={4}>UID: {member.uid.substring(0, 8)} • {member.age} yrs • {member.gender === 'male' ? 'Male' : member.gender === 'female' ? 'Female' : 'Other'}</Text>
                                    </Box>
                                </Group>

                                <SimpleGrid cols={2} spacing="md" mb="xl">
                                    <Box sx={{ backgroundColor: '#f8f9fa', padding: '16px', borderRadius: '16px' }}>
                                        <Group spacing={4} mb={8}>
                                            <ThemeIcon size="xs" variant="transparent" color="gray"><IconTarget size={14} /></ThemeIcon>
                                            <Text size="xs" color="dimmed" weight={600}>Weight</Text>
                                        </Group>
                                        <Group align="flex-end" spacing={4}>
                                            <Text size="xl" weight={800}>{member.weight}</Text>
                                            <Text size="sm" color="dimmed" mb={4}>kg</Text>
                                        </Group>
                                        <Text size="xs" color="dimmed" mt={4}>Target: {member.target_weight || 'N/A'} kg</Text>
                                    </Box>
                                    <Box sx={{ backgroundColor: '#f8f9fa', padding: '16px', borderRadius: '16px' }}>
                                        <Group spacing={4} mb={8}>
                                            <ThemeIcon size="xs" variant="transparent" color="gray"><IconActivity size={14} /></ThemeIcon>
                                            <Text size="xs" color="dimmed" weight={600}>BMI</Text>
                                        </Group>
                                        <Text size="xl" weight={800}>{member.bmi}</Text>
                                        <Badge color="green" variant="light" size="xs" mt={4}>Healthy Range</Badge>
                                    </Box>
                                    <Box sx={{ backgroundColor: '#f8f9fa', padding: '16px', borderRadius: '16px' }}>
                                        <Group spacing={4} mb={8}>
                                            <ThemeIcon size="xs" variant="transparent" color="gray"><IconUser size={14} /></ThemeIcon>
                                            <Text size="xs" color="dimmed" weight={600}>Age</Text>
                                        </Group>
                                        <Group align="flex-end" spacing={4}>
                                            <Text size="xl" weight={800}>{member.age}</Text>
                                            <Text size="sm" color="dimmed" mb={4}>yrs</Text>
                                        </Group>
                                    </Box>
                                    <Box sx={{ backgroundColor: '#f8f9fa', padding: '16px', borderRadius: '16px' }}>
                                        <Group spacing={4} mb={8}>
                                            <ThemeIcon size="xs" variant="transparent" color="gray"><IconActivity size={14} /></ThemeIcon>
                                            <Text size="xs" color="dimmed" weight={600}>Height</Text>
                                        </Group>
                                        <Group align="flex-end" spacing={4}>
                                            <Text size="xl" weight={800}>{member.height}</Text>
                                            <Text size="sm" color="dimmed" mb={4}>cm</Text>
                                        </Group>
                                    </Box>
                                </SimpleGrid>

                                <Stack spacing="md">
                                    <Group noWrap>
                                        <ThemeIcon size="md" radius="xl" color="red" variant="light"><IconTarget size={16} /></ThemeIcon>
                                        <Box>
                                            <Text size="xs" color="dimmed" weight={600}>Goal</Text>
                                            <Text size="sm" weight={600} sx={{ color: '#2c2e33' }}>{(member.goal || 'General Fitness').replace('_', ' ').toUpperCase()}</Text>
                                        </Box>
                                    </Group>
                                    <Group noWrap>
                                        <ThemeIcon size="md" radius="xl" color="blue" variant="light"><IconBolt size={16} /></ThemeIcon>
                                        <Box>
                                            <Text size="xs" color="dimmed" weight={600}>Activity Level</Text>
                                            <Text size="sm" weight={600} sx={{ color: '#2c2e33' }} transform="capitalize">{(member.activity_level || 'N/A').replace('_', ' ')}</Text>
                                        </Box>
                                    </Group>
                                    <Group noWrap>
                                        <ThemeIcon size="md" radius="xl" color="gray" variant="light"><IconHome size={16} /></ThemeIcon>
                                        <Box>
                                            <Text size="xs" color="dimmed" weight={600}>Location</Text>
                                            <Text size="sm" weight={600} sx={{ color: '#2c2e33' }} transform="capitalize">{member.workout_location || 'Gym'}</Text>
                                        </Box>
                                    </Group>
                                </Stack>
                            </Card>
                        </Grid.Col>

                        {/* RIGHT COLUMN - ROUTINE CONSTRAINTS */}
                        <Grid.Col span={12} md={8}>
                            <Card radius="lg" p="xl" shadow="sm" withBorder sx={{ borderColor: '#f1f3f5', height: '100%' }}>
                                <Title order={4} mb="xl" sx={{ color: '#2c2e33' }}>Routine Constraints</Title>

                                <Grid gutter="xl">
                                    <Grid.Col span={6}>
                                        <Select
                                            label={<Text size="sm" weight={600} color="dimmed" mb="xs"><IconTarget size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Selection Goal <span style={{ color: '#fa5252' }}>*</span></Text>}
                                            data={[{ value: 'weight_loss', label: 'Weight Loss' }, { value: 'muscle_gain', label: 'Muscle Gain' }, { value: 'strength', label: 'Strength' }, { value: 'endurance', label: 'Endurance' }, { value: 'maintain_fitness', label: 'Maintain Fitness' }]}
                                            value={inputs.goal}
                                            onChange={(val: string) => setInputs({ ...inputs, goal: val })}
                                            radius="xl"
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={6}>
                                        <Select
                                            label={<Text size="sm" weight={600} color="dimmed" mb="xs"><IconBolt size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Complexity Level <span style={{ color: '#fa5252' }}>*</span></Text>}
                                            data={[{ value: 'beginner', label: 'Beginner' }, { value: 'intermediate', label: 'Intermediate' }, { value: 'advanced', label: 'Advanced' }]}
                                            value={inputs.level}
                                            onChange={(val: string) => setInputs({ ...inputs, level: val })}
                                            radius="xl"
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={4}>
                                        <Select
                                            label={<Text size="sm" weight={600} color="dimmed" mb="xs"><IconClock size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Days Per Week <span style={{ color: '#fa5252' }}>*</span></Text>}
                                            data={getDaysOptions(inputs.level)}
                                            value={inputs.daysPerWeek}
                                            onChange={handleDaysPerWeekChange}
                                            radius="xl"
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={4}>
                                        <Select
                                            label={<Text size="sm" weight={600} color="dimmed" mb="xs"><IconClock size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Duration <span style={{ color: '#fa5252' }}>*</span></Text>}
                                            data={[{ value: '30', label: '30' }, { value: '45', label: '45' }, { value: '60', label: '60' }, { value: '60+', label: '60+' }]}
                                            value={inputs.duration}
                                            onChange={(val: string) => setInputs({ ...inputs, duration: val })}
                                            radius="xl"
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={4}>
                                        <Select
                                            label={<Text size="sm" weight={600} color="dimmed" mb="xs"><IconHome size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Location <span style={{ color: '#fa5252' }}>*</span></Text>}
                                            data={[{ value: 'gym', label: 'Gym' }, { value: 'home', label: 'Home' }, { value: 'travel', label: 'Travel' }]}
                                            value={inputs.place}
                                            onChange={(val: string) => setInputs({ ...inputs, place: val })}
                                            radius="xl"
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={12}>
                                        <MultiSelect
                                            label={<Text size="sm" weight={600} color="dimmed" mb="xs"><IconBarbell size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Available Equipment</Text>}
                                            placeholder="Select equipment..."
                                            data={['Bodyweight', 'Dumbbell', 'Barbell', 'Kettlebell', 'Cable', 'Resistance Band', 'Machine', 'Medicine Ball']}
                                            searchable
                                            value={inputs.equipment}
                                            onChange={(val: string[]) => setInputs({ ...inputs, equipment: val })}
                                            radius="xl"
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={12}>
                                        <Box>
                                            <Text size="sm" weight={600} color="dimmed" mb="xs">
                                                <IconClock size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Target Days <span style={{ color: '#fa5252' }}>*</span>
                                            </Text>
                                            <Group spacing={8}>
                                                {[
                                                    { label: 'M', value: 'Monday' },
                                                    { label: 'T', value: 'Tuesday' },
                                                    { label: 'W', value: 'Wednesday' },
                                                    { label: 'T', value: 'Thursday' },
                                                    { label: 'F', value: 'Friday' },
                                                    { label: 'S', value: 'Saturday' },
                                                    { label: 'S', value: 'Sunday' },
                                                ].map((day, idx) => {
                                                    const isSelected = targetDays.includes(day.value);
                                                    return (
                                                        <UnstyledButton
                                                            key={`${day.value}-${idx}`}
                                                            onClick={() => {
                                                                if (isSelected) {
                                                                    setTargetDays(targetDays.filter(d => d !== day.value));
                                                                } else {
                                                                    const requiredDays = Number(inputs.daysPerWeek) || 0;
                                                                    if (requiredDays > 0 && targetDays.length >= requiredDays) {
                                                                        toast.warn(`You can only select exactly ${requiredDays} Target Days.`);
                                                                        return;
                                                                    }
                                                                    setTargetDays([...targetDays, day.value]);
                                                                }
                                                            }}
                                                            sx={(theme: any) => ({
                                                                width: 38, height: 38, borderRadius: '50%',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                fontWeight: 600, fontSize: theme.fontSizes.sm,
                                                                color: isSelected ? theme.white : theme.colors.gray[6],
                                                                backgroundColor: isSelected ? theme.colors.blue[6] : theme.colors.gray[1],
                                                                border: `1px solid ${isSelected ? theme.colors.blue[6] : theme.colors.gray[3]}`,
                                                                transition: 'all 0.2s ease',
                                                                '&:hover': { backgroundColor: isSelected ? theme.colors.blue[7] : theme.colors.gray[2] }
                                                            })}
                                                        >
                                                            {day.label}
                                                        </UnstyledButton>
                                                    )
                                                })}
                                            </Group>
                                            <Text size="xs" color="dimmed" mt={4}>
                                                {inputs?.place?.toLowerCase() === 'gym' 
                                                    ? "Remaining days are rest days or light workout days." 
                                                    : "Remaining days are rest days."}
                                            </Text>
                                        </Box>
                                    </Grid.Col>
                                </Grid>


                            </Card>
                        </Grid.Col>
                    </Grid>
                ) : (
                    <Card shadow="sm" p="xl" radius="md" withBorder sx={{ textAlign: 'center', backgroundColor: '#fff', borderColor: '#f1f3f5', marginTop: '20px' }}>
                        <IconUser size={50} color="#ced4da" style={{ marginBottom: 15 }} />
                        <Title order={3} mb="sm" sx={{ color: '#495057' }}>No Member Selected</Title>
                        <Text size="md" color="dimmed" mb="lg">
                            Please select a branch and a member from the dropdowns above to view their details and generate an AI-powered fitness routine.
                        </Text>
                    </Card>
                )}

                {/* UNIFIED GENERATED ROUTINE CARD */}
                {filteredExercises.length > 0 && (
                    <Card radius="lg" p={0} shadow="sm" withBorder sx={{ borderColor: '#f1f3f5', marginTop: '40px', marginBottom: '40px', overflow: 'hidden' }}>
                        {/* RED HEADER SECTION */}
                        <Box sx={{ backgroundColor: '#ee374d', color: '#fff', padding: '32px 40px' }}>
                            <Group position="apart">
                                <Box>
                                    <Group spacing="xs" mb={8}>
                                        <ThemeIcon size="sm" radius="xl" color="white" variant="light"><IconBolt size={12} color="#fff" /></ThemeIcon>
                                        <Text size="xs" weight={700} sx={{ letterSpacing: 1 }}>AI Generated</Text>
                                    </Group>
                                    <Title order={2}>{filteredExercises.length} Day Routine Generated</Title>
                                    <Text size="sm" mt={8} sx={{ opacity: 0.9 }}>Optimized for {(inputs.goal || '').replace('_', ' ').toUpperCase()} • {inputs.duration} min sessions</Text>
                                </Box>
                                <Box>
                                    <Text size="xs" weight={700} mb="sm" sx={{ letterSpacing: 1, opacity: 0.9 }}>PROPOSED SPLIT</Text>
                                    <Group spacing="sm">
                                        {filteredExercises.map((dayData, idx) => (
                                            <React.Fragment key={idx}>
                                                <Box sx={{ backgroundColor: 'rgba(255,255,255,0.2)', padding: '12px 24px', borderRadius: '12px' }}>
                                                    <Text size="xs" weight={700} sx={{ opacity: 0.8 }}>Day {idx + 1}</Text>
                                                    <Text size="md" weight={700}>{dayData.dayName}</Text>
                                                </Box>
                                                {idx < filteredExercises.length - 1 && <IconChevronRight size={20} color="rgba(255,255,255,0.5)" />}
                                            </React.Fragment>
                                        ))}
                                    </Group>
                                </Box>
                            </Group>
                        </Box>

                        {/* EXERCISES ACCORDION AREA */}
                        <Box p="xl" sx={{ backgroundColor: '#fff' }}>
                            <Accordion variant="separated" chevron={<IconChevronDown size={20} />} styles={{
                                item: {
                                    backgroundColor: '#fafafa',
                                    border: '1px solid #f1f3f5',
                                    borderRadius: '16px',
                                    marginBottom: '16px'
                                },
                                control: { padding: '24px', borderRadius: '16px', '&:hover': { backgroundColor: '#f1f3f5' } },
                                panel: { padding: '0 24px 24px 24px' }
                            }}>
                                {filteredExercises.map((dayData, dayIndex) => {
                                    const dayExercises = dayData.exercises;
                                    const totalDayTimeSeconds = dayExercises.reduce((acc: any, ex: any) => acc + (ex.timeSeconds || 0), 0);
                                    const totalDayCalories = dayExercises.reduce((acc: any, ex: any) => acc + (ex.calories || 0), 0);
                                    const totalDayMin = Math.round(totalDayTimeSeconds / 60);

                                    return (
                                        <Accordion.Item key={dayIndex} value={`day-${dayIndex}`}>
                                            <Accordion.Control>
                                                <Group position="apart" w="100%" noWrap>
                                                    <Group noWrap>
                                                        <Avatar size={48} radius="xl" color="dark" sx={{ backgroundColor: '#2c2e33', color: '#fff', fontSize: '20px' }}>
                                                            {dayData.dayName.charAt(0)}
                                                        </Avatar>
                                                        <Box>
                                                            <Title order={4} sx={{ color: '#2c2e33' }}>{dayData.dayName}</Title>
                                                            <Text size="sm" color="dimmed">{dayExercises.length} Exercises</Text>
                                                        </Box>
                                                    </Group>
                                                    <Group spacing="xl">
                                                        <Group spacing={6}>
                                                            <IconBolt size={16} color="#ee374d" />
                                                            <Box>
                                                                <Text size="xs" color="dimmed" weight={600}>Difficulty</Text>
                                                                <Text size="sm" weight={700}>Intermediate</Text>
                                                            </Box>
                                                        </Group>
                                                        <Group spacing={6}>
                                                            <IconActivity size={16} color="#3b82f6" />
                                                            <Box>
                                                                <Text size="xs" color="dimmed" weight={600}>Fatigue</Text>
                                                                <Text size="sm" weight={700} sx={{ textTransform: 'capitalize' }}>{dayData.fatigue?.replace('_', ' ') || 'Moderate'}</Text>
                                                            </Box>
                                                        </Group>
                                                        <Group spacing={6}>
                                                            <IconClock size={16} color="#868e96" />
                                                            <Box>
                                                                <Text size="xs" color="dimmed" weight={600}>Duration</Text>
                                                                <Text size="sm" weight={700}>{totalDayMin} min</Text>
                                                            </Box>
                                                        </Group>
                                                        <Group spacing={6}>
                                                            <IconFlame size={16} color="#ee374d" />
                                                            <Box>
                                                                <Text size="xs" color="dimmed" weight={600}>Calories</Text>
                                                                <Text size="sm" weight={700}>{totalDayCalories} kcal</Text>
                                                            </Box>
                                                        </Group>
                                                    </Group>
                                                </Group>
                                            </Accordion.Control>
                                            <Accordion.Panel>
                                                <Stack spacing="md">
                                                    <Divider variant="dashed" mb="md" />
                                                    {dayExercises.map((ex: any, idx: any) => (
                                                        <ExerciseCardRedesigned key={idx} exercise={ex} />
                                                    ))}
                                                </Stack>
                                            </Accordion.Panel>
                                        </Accordion.Item>
                                    );
                                })}
                            </Accordion>
                        </Box>
                    </Card>
                )}
            </Container>
        </ErrorBoundary>
    );
};

// --- HELPER COMPONENTS ---

const PillSelector = ({ options, value, onChange }: { options: { value: string, label: string }[], value: string, onChange: (val: string) => void }) => (
    <Group spacing="sm">
        {options.map(opt => (
            <Box
                key={opt.value}
                onClick={() => onChange(opt.value)}
                sx={{
                    padding: '8px 16px',
                    borderRadius: '20px',
                    backgroundColor: value === opt.value ? '#f1f3f5' : '#fff',
                    border: value === opt.value ? '1px solid #dee2e6' : '1px solid #f1f3f5',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    fontWeight: value === opt.value ? 600 : 500,
                    color: value === opt.value ? '#212529' : '#868e96',
                    '&:hover': { backgroundColor: '#f8f9fa' }
                }}
            >
                <Text size="sm">{opt.label}</Text>
            </Box>
        ))}
    </Group>
);

const DaysSelector = ({ options, value, onChange }: { options: { value: string, label: string }[], value: string, onChange: (val: string) => void }) => (
    <Group spacing="xs">
        {options.map(opt => (
            <Box
                key={opt.value}
                onClick={() => onChange(opt.value)}
                sx={{
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    backgroundColor: value === opt.value ? '#ee374d' : '#f8f9fa',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    fontWeight: 600,
                    color: value === opt.value ? '#fff' : '#495057',
                    '&:hover': { backgroundColor: value === opt.value ? '#d92c40' : '#e9ecef' }
                }}
            >
                {opt.label.split(' ')[0]}
            </Box>
        ))}
    </Group>
);

const ExerciseCardRedesigned = ({ exercise }: { exercise: any }) => (
    <Box sx={{ borderBottom: '1px solid #f1f3f5', paddingBottom: '24px' }}>
        <Grid align="center" gutter="xl">
            {/* Left section: Icon, Name, Tags */}
            <Grid.Col span={12} sm={4}>
                <Group noWrap align="flex-start">
                    <Box sx={{ backgroundColor: '#f8f9fa', padding: '12px', borderRadius: '12px' }}>
                        <IconBarbell size={24} color="#adb5bd" />
                    </Box>
                    <Box>
                        <Text weight={700} size="md" sx={{ color: '#2c2e33' }}>{exercise.name}</Text>
                        <Group spacing={4} mt={4} mb={8}>
                            <Badge size="xs" color="gray" variant="light">{exercise.level || 'Intermediate'}</Badge>
                            <Text size="xs" color="dimmed">•</Text>
                            <Text size="xs" color="dimmed">{exercise.category || 'Compound'}</Text>
                        </Group>
                        <Group spacing={4}>
                            {exercise.primary_muscle?.map((m: string, i: number) => (
                                <Text key={`pm-${i}`} size="xs" sx={{ color: '#ee374d', backgroundColor: '#fdf2f3', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>{m}</Text>
                            ))}
                            {exercise.equipment?.map((eq: string, i: number) => (
                                <Text key={`eq-${i}`} size="xs" sx={{ color: '#495057', backgroundColor: '#f1f3f5', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>{eq}</Text>
                            ))}
                        </Group>
                    </Box>
                </Group>
            </Grid.Col>

            {/* Right section: Metrics */}
            <Grid.Col span={12} sm={8}>
                <SimpleGrid cols={3} spacing="md">
                    {/* Weight Target */}
                    <Box sx={{ backgroundColor: '#f8f9fa', padding: '12px 16px', borderRadius: '12px' }}>
                        <Group spacing={4} mb={4}>
                            <IconTarget size={14} color="#adb5bd" />
                            <Text size="xs" color="dimmed" weight={600}>Weight Target</Text>
                        </Group>
                        <Text weight={700} size="sm">{exercise.suggestedWeight || 'Bodyweight'}</Text>
                    </Box>
                    {/* Sets x Reps */}
                    <Box sx={{ backgroundColor: '#f8f9fa', padding: '12px 16px', borderRadius: '12px' }}>
                        <Group spacing={4} mb={4}>
                            <IconClock size={14} color="#adb5bd" />
                            <Text size="xs" color="dimmed" weight={600}>Sets x Reps</Text>
                        </Group>
                        <Text weight={700} size="sm">{exercise.sets || 3} × {exercise.reps_time || '10-12'}</Text>
                    </Box>
                    {/* Rest Time */}
                    <Box sx={{ backgroundColor: '#f8f9fa', padding: '12px 16px', borderRadius: '12px' }}>
                        <Group spacing={4} mb={4}>
                            <IconClock size={14} color="#adb5bd" />
                            <Text size="xs" color="dimmed" weight={600}>Rest Time</Text>
                        </Group>
                        <Text weight={700} size="sm">{exercise.restPerSet || 60} sec</Text>
                    </Box>
                    {/* Duration */}
                    <Box sx={{ backgroundColor: '#f8f9fa', padding: '12px 16px', borderRadius: '12px' }}>
                        <Group spacing={4} mb={4}>
                            <IconActivity size={14} color="#adb5bd" />
                            <Text size="xs" color="dimmed" weight={600}>Duration</Text>
                        </Group>
                        <Text weight={700} size="sm">{exercise.timeSeconds ? `${Math.round(exercise.timeSeconds / 60)} min` : '5 min'}</Text>
                    </Box>
                    {/* Calories */}
                    <Box sx={{ backgroundColor: '#f8f9fa', padding: '12px 16px', borderRadius: '12px' }}>
                        <Group spacing={4} mb={4}>
                            <IconFlame size={14} color="#adb5bd" />
                            <Text size="xs" color="dimmed" weight={600}>Calories</Text>
                        </Group>
                        <Text weight={700} size="sm">{exercise.calories || 50} kcal</Text>
                    </Box>
                </SimpleGrid>
            </Grid.Col>
        </Grid>
    </Box>
);

export default MemberRoutineGenerator;
